
import {
  computed, defineComponent, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmPopup from 'primevue/confirmpopup';
import useDateFormat from '@/composables/useDateFormat';
import { useI18n } from 'vue-i18n';
import SpmTableApi from '@/components/table/SpmTableApi.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import ImportModal from '@/components/modals/ImportModal.vue';
import ImportModalNew from '@/components/import/ImportModal.vue';
import CampaignAutomationSelectorV2 from '@/components/automated-scenarios/CampaignAutomationSelectorV2.vue';
import {
  CampaignAutomationStatusEnum,
  SpmTableAction,
  SpmTableColumns,
  SpmTableFilter,
  SpmTableSort,
  TypeCampaignEnum,
} from '@/types';
import {
  CampaignsAutomation,
  CampaignsAutomationUpdateInputItem,
  OperatorType,
  CampaignsAutomationDuplicateInputItem,
} from '@/types/generated-types/graphql';
import {
  AutomatedScenarioState as stateAutomatedScenario,
  setRefreshScenariosList,
  updateCampaignAutomationListColumn,
  getAutomatedCampaignOperators,
  toggleWebhooksForAutomationCampaign,
  activateCampaign,
  setAnalyticsMode,
  togglePeriodicTriggersForAutomationCampaign,
  deleteWebhooksForAutomationCampaign
} from '@/composables/AutomatedScenarios';
import { findPermission, UserState, hasAccessToFeatures } from '@/composables/User';
import CampaignPerformanceMetrics from '@/components/automated-scenarios/CampaignPerformanceMetrics.vue';
import type { CampaignMetricDisplay } from '@/components/automated-scenarios/CampaignPerformanceMetrics.vue';
import { store } from '@/store';
import { getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { registerExportRequest } from '@/composables/useExport';
import { showToastError, showToastSuccess } from '@/helpers';
import type { MenuItem } from 'primevue/menuitem';
import moment from 'moment/moment';
import {
  getChannelCardStyleVars,
  getChannelIcon,
  getChannelTagStyleVars,
  getStatusIcon,
  getStatusStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';

const percentageFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const amountFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default defineComponent({
  name: 'AutomatedScenarioList',
  components: {
    SpmButton,
    SpmTableApi,
    ImportModal,
    ImportModalNew,
    ConfirmPopup,
    CampaignAutomationSelectorV2,
    CampaignPerformanceMetrics,
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);
    type SpmTableApiInstance = InstanceType<typeof SpmTableApi> & {
      unselectAllRows?: () => void;
      toggleDialog?: (...args: any[]) => void;
      reloadData?: () => void;
    };

    const tableRef = ref<SpmTableApiInstance | null>(null);
    const openWorkflowSelector = ref(false);
    const isImportModalVisible = ref(false);
    const isNewImportModalVisible = ref(false);
    const isProcessingAction = ref(false);
    const dataExportPermission = hasAccessToFeatures('data.campaign.export');

    const confirm = useConfirm();

    const registerExportRequestCommand = async (idsTobeExported: number[]) => {
      try {
        await registerExportRequest(
          idsTobeExported,
          idShop.value,
          UserState.user.id,
          UserState.user.userType,
          'automation',
        );
        await showToastSuccess(t('campaigns.automation.export.exportSuccess'));
        tableRef.value?.unselectAllRows?.();
      } catch (error) {
        await showToastError(t('campaigns.automation.export.exportFailure'));
      }
    };

    const hiddenStatuses: CampaignAutomationStatusEnum[] = [
      CampaignAutomationStatusEnum.ARCHIVED,
      CampaignAutomationStatusEnum.DELETED,
    ];

    const statusOptions = computed(() =>
      (Object.values(CampaignAutomationStatusEnum) as CampaignAutomationStatusEnum[])
        .filter((status) => !hiddenStatuses.includes(status))
        .map((status) => ({
          value: status,
          label: t(`campaigns.automation.list.status.${status}`),
        })),
    );

    const persistentFilters = computed<SpmTableFilter[]>(() =>
      hiddenStatuses.map((status) => ({
        field: 'status',
        value: status,
        operator: OperatorType.NotEquals,
      })),
    );

    const openScenarioAnalytics = async (scenario: any) => {
      if (!scenario?.id || isProcessingAction.value) {
        return;
      }

      try {
        isProcessingAction.value = true;
        store.commit('general/showTheSpinner');
        await activateCampaign(scenario.id, TypeCampaignEnum.AUTOMATION);
        await setAnalyticsMode(true);
      } catch (error: any) {
        await showToastError(error?.message ?? t('errorMessages.GENERIC_ERROR'));
      } finally {
        store.commit('general/hideTheSpinner');
        isProcessingAction.value = false;
      }
    };

    //const typeOptions: SpmTableFilterOption[] = Object.values(CampaignAutomationTypeEnum).map((key: string) => ({ value: key, label: t(`automatedScenarios.${key}`) }));

    const confirmationDialog = (campaignAutomation: CampaignsAutomation, event: any) => {
      const workflow = ref(campaignAutomation);
      const isActivated = workflow.value.status === CampaignAutomationStatusEnum.ACTIVE;

      // Si on désactive un scénario, on utilise la confirmation standard
      if (isActivated) {
        confirm.require({
          group: 'basicGroup',
          target: event.target,
          message: isActivated ? t('campaigns.automation.list.actions.disable.message') : t('campaigns.automation.list.actions.activate.message'),
          header: t('confirmation'),
          icon: 'far fa-exclamation-triangle',
          acceptLabel: t('yes'),
          rejectLabel: t('no'),
          acceptClass: 'p-button-success',
          rejectClass: 'p-button-secondary',
          accept: async () => {
            const input: CampaignsAutomationUpdateInputItem = {
              id_campaign_automation: workflow.value.id_campaign_automation,
              id_shop: UserState.activeShop?.id ?? 0,
              status: isActivated ? CampaignAutomationStatusEnum.DISABLED : CampaignAutomationStatusEnum.ACTIVE,
              date_modification: moment().format('YYYY-MM-DD HH:mm:ss'),
            };
            await updateCampaignAutomationListColumn([input]).then((result: any) => {
              showToastSuccess(isActivated ? t('campaigns.automation.list.actions.disabled') : t('campaigns.automation.list.actions.activated'));
              workflow.value.status = isActivated ? CampaignAutomationStatusEnum.DISABLED : CampaignAutomationStatusEnum.ACTIVE;
              // Activation/deactivation of workflow's webhooks
              toggleWebhooksForAutomationCampaign(workflow.value.id_campaign_automation, !isActivated);

              // Activation/deactivation of workflow's periodic triggers
              togglePeriodicTriggersForAutomationCampaign(workflow.value.id_campaign_automation, !isActivated);
            }).catch((err: Error) => {
              showToastError(err.message);
            });
          },
        });
      } else {
        // Vérification de l'offre du shop pour l'activation
        const hasOfferV4 = UserState.activeOffer && Object.keys(UserState.activeOffer).length > 0;
        const hasOfferV3 = UserState.hasOfferV3 === true && UserState.offerV3 && ['1', '3'].includes(UserState.offerV3.bundle_options);

        // Si le shop n'a pas d'offre V3 ou V4, on affiche une modale pour rediriger vers myOffer
        if (!hasOfferV4 && !hasOfferV3) {
          confirm.require({
            group: 'basicGroup',
            target: event.target,
            message: t('automatedScenarios.insufficientOffer'),
            header: t('attention'),
            icon: 'far fa-exclamation-triangle',
            acceptLabel: t('offers.updatePlanButton'),
            rejectLabel: t('cancel'),
            accept: async () => {
              await router.push({ name: 'shop.MyOffer' });
            },
          });
        } else {
          // Si le shop a une offre, on utilise la confirmation standard pour l'activation
          confirm.require({
            group: 'basicGroup',
            target: event.target,
            message: t('campaigns.automation.list.actions.activate.message'),
            header: t('confirmation'),
            icon: 'far fa-exclamation-triangle',
            acceptLabel: t('yes'),
            rejectLabel: t('no'),
            acceptClass: 'p-button-success',
            rejectClass: 'p-button-secondary',
            accept: async () => {
              const input: CampaignsAutomationUpdateInputItem = {
                id_campaign_automation: workflow.value.id_campaign_automation,
                id_shop: UserState.activeShop?.id ?? 0,
                status: isActivated ? CampaignAutomationStatusEnum.DISABLED : CampaignAutomationStatusEnum.ACTIVE,
                date_modification: moment().format('YYYY-MM-DD HH:mm:ss'),
              };
              await updateCampaignAutomationListColumn([input]).then((result: any) => {
                showToastSuccess(isActivated ? t('campaigns.automation.list.actions.disabled') : t('campaigns.automation.list.actions.activated'));
                workflow.value.status = isActivated ? CampaignAutomationStatusEnum.DISABLED : CampaignAutomationStatusEnum.ACTIVE;

                // Activation/deactivation of workflow's webhooks
                toggleWebhooksForAutomationCampaign(workflow.value.id_campaign_automation, !isActivated);

                // Activation/deactivation of workflow's periodic triggers
                togglePeriodicTriggersForAutomationCampaign(workflow.value.id_campaign_automation, !isActivated);
              }).catch((err: Error) => {
                showToastError(err.message);
              });
            },
          });
        }
      }
    };

    const openScenarioEditor = async (scenario: any) => {
      if (!scenario?.id || isProcessingAction.value) {
        return;
      }

      try {
        isProcessingAction.value = true;
        const notConfiguredServices = await getNotConfiguredServices(idShop.value, ['editScenarioCampaign']);
        if (notConfiguredServices.length) {
          store.commit('general/setNotConfiguredServices', notConfiguredServices);
          store.commit('general/setIsServicesParametersModalVisible', true);
          isProcessingAction.value = false;
          return;
        }

        store.commit('general/showTheSpinner');
        await activateCampaign(scenario.id, TypeCampaignEnum.AUTOMATION);
      } catch (error: any) {
        await showToastError(error?.message ?? t('errorMessages.GENERIC_ERROR'));
      } finally {
        store.commit('general/hideTheSpinner');
        isProcessingAction.value = false;
      }
    };

    const groupActions = computed<MenuItem[]>(() => [
      {
        code: 'export',
        label: t('campaigns.automation.list.groupedActions.export.menuItem'),
        command: () => {
          const execute = () => {
            if (!tableRef.value) {
              return;
            }

            tableRef.value?.toggleDialog?.(
              '',
              t('campaigns.automation.list.groupedActions.export.text'),
              t('campaigns.automation.list.groupedActions.export.formTitle'),
              '',
              t('yes'),
              t('no'),
              'custom',
              null,
              '',
              registerExportRequestCommand,
            );
          };
          execute();
        },
      },
    ]);

    const actionItems = computed<SpmTableAction[]>(() => [
      {
        icon: 'far fa-eye',
        label: t('campaigns.common.actions.viewReport'),
        show: () => findPermission('automated_scenarios.view'),
        callback: (scenario: any) => openScenarioAnalytics(scenario),
      },
      {
        icon: 'far fa-edit',
        label: t('campaigns.automation.list.actions.edit'),
        show: () => findPermission('automated_scenarios.edit'),
        callback: (scenario: any) => openScenarioEditor(scenario),
      },
    ]);

    const columns = computed<SpmTableColumns[]>(() => [
      {
        field: 'name',
        header: t('campaigns.automation.list.columns.name'),
        sortable: true,
        filterable: true,
        editable: false,
        style: 'min-width: 240px',
        type: 'text',
        controls: [
          {
            key: 'automation-date-creation',
            field: 'date_creation',
            label: t('campaigns.common.columns.createdAt'),
            sortable: true,
            filterable: true,
            type: 'datetime',
            filterSettings: { type: 'date', hideFilterMenu: true },
          },
          {
            key: 'automation-date-modification',
            field: 'date_modification',
            label: t('campaigns.common.columns.lastModified'),
            sortable: true,
            filterable: true,
            type: 'datetime',
            filterSettings: { type: 'date', hideFilterMenu: true },
          },
        ],
      },
      {
        field: 'status',
        header: t('campaigns.automation.list.columns.status'),
        sortable: true,
        filterable: true,
        editable: false,
        style: 'min-width: 140px',
        type: 'text',
        filterSettings: {
          type: 'dropdown',
          options: statusOptions.value,
          defaultMatchMode: OperatorType.Equals,
        },
      },
      {
        field: 'engagement_summary',
        header: t('campaigns.common.headers.performance'),
        sortable: false,
        filterable: false,
        editable: false,
        style: 'min-width: 260px',
        type: 'text',
        custom: true,
        controls: [
          {
            key: 'automation-engagement-open-rate',
            field: 'open_rate',
            label: t('campaigns.common.metrics.openRate'),
            sortable: true,
          },
          {
            key: 'automation-engagement-click-rate',
            field: 'click_rate',
            label: t('campaigns.common.metrics.clickRate'),
            sortable: true,
          },
          {
            key: 'automation-engagement-deliveries',
            field: 'deliveries',
            label: t('campaigns.common.metrics.deliveries'),
            sortable: true,
          },
          {
            key: 'automation-engagement-opens',
            field: 'opens',
            label: t('campaigns.common.metrics.opens'),
            sortable: true,
          },
          {
            key: 'automation-engagement-open-unique',
            field: 'open_unique',
            label: t('campaigns.analytics.sendStatistics.items.labels.open_unique'),
            sortable: true,
          },
          {
            key: 'automation-engagement-clicks',
            field: 'clicks',
            label: t('campaigns.common.metrics.clicks'),
            sortable: true,
          },
          {
            key: 'automation-engagement-click-unique',
            field: 'click_unique',
            label: t('campaigns.analytics.sendStatistics.items.labels.click_unique'),
            sortable: true,
          },
        ],
      },
      {
        field: 'conversion_summary',
        header: t('campaigns.common.headers.conversions'),
        sortable: false,
        filterable: false,
        editable: false,
        style: 'min-width: 220px',
        type: 'text',
        custom: true,
        controls: [
          {
            key: 'automation-conversion-sales-amount',
            field: 'sales_amount',
            label: t('campaigns.common.metrics.salesAmount'),
            sortable: true,
          },
          {
            key: 'automation-conversion-rate',
            field: 'conversion_rate',
            label: t('campaigns.common.metrics.conversionRate'),
            sortable: true,
          },
          {
            key: 'automation-conversion-sales',
            field: 'sales',
            label: t('campaigns.common.metrics.sales'),
            sortable: true,
          },
          {
            key: 'automation-conversion-bounces',
            field: 'bounces',
            label: t('campaigns.common.metrics.bounces'),
            sortable: true,
          },
          {
            key: 'automation-conversion-spam',
            field: 'spam',
            label: t('campaigns.common.metrics.spam'),
            sortable: true,
          },
          {
            key: 'automation-conversion-unsubscribes',
            field: 'unsubscribes',
            label: t('campaigns.common.metrics.unsubscribes'),
            sortable: true,
          },
        ],
      },
      {
        field: 'actions',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'width: 4rem',
        type: 'action',
        custom: true,
        actions: actionItems.value,
      },
      {
        field: 'deliveries',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'opens',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'clicks',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'conversion_rate',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'open_rate',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'click_rate',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'primary_channel',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'channels',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'sales',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'sales_amount',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'unsubscribes',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'bounces',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'spam',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'revenue_per_send',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'text',
        hidden: true,
      },
      {
        field: 'date_creation',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: 'display:none',
        type: 'datetime',
        hidden: true,
      },
    ]);

    const initialSorting = ref<SpmTableSort[]>([
      { field: 'date_modification', type: 'desc' },
    ]);

    const showImportModal = () => {
      isImportModalVisible.value = true;
    };

    const hideImportModal = () => {
      isImportModalVisible.value = false;
    };

    const showNewImportModal = () => {
      isNewImportModalVisible.value = true;
    };

    const hideNewImportModal = () => {
      isNewImportModalVisible.value = false;
    };

    const handleCreateScenario = () => {
      openWorkflowSelector.value = true;
    };

    const reloadTable = () => {
      if (tableRef.value) {
        tableRef.value.reloadData();
      }
    };

    watch(
      () => stateAutomatedScenario.refreshScenariosList,
      (shouldRefresh) => {
        if (shouldRefresh) {
          reloadTable();
          setRefreshScenariosList(false);
        }
      },
    );

    onMounted(() => {
      setRefreshScenariosList(false);
    });

    const formatPercentage = (value: number): string => {
      if (!Number.isFinite(value)) {
        return t('campaigns.common.placeholders.noData');
      }

      const ratio = value / 100;
      return percentageFormatter.format(ratio);
    };

    const formatInteger = (value: number | null | undefined): string => {
      if (value === null || value === undefined) {
        return t('campaigns.common.placeholders.noData');
      }
      return integerFormatter.format(value);
    };

    const formatAmount = (value: number | null | undefined): string => {
      if (value === null || value === undefined) {
        return t('campaigns.common.placeholders.noData');
      }

      return amountFormatter.format(value);
    };

    const formatCombinedMetric = (
      primary: number | null | undefined,
      secondary: number | null | undefined,
    ): string => {
      const isPrimaryMissing = primary === null || primary === undefined;
      const isSecondaryMissing = secondary === null || secondary === undefined;

      if (isPrimaryMissing && isSecondaryMissing) {
        return t('campaigns.common.placeholders.noData');
      }

      const placeholder = t('campaigns.common.placeholders.noData');
      const primaryText = isPrimaryMissing
        ? placeholder
        : integerFormatter.format(primary as number);
      const secondaryText = isSecondaryMissing
        ? placeholder
        : integerFormatter.format(secondary as number);

      return `${primaryText} / ${secondaryText}`;
    };

    const formatDateTime = (value: string | Date | null): string => {
      if (!value) {
        return t('campaigns.common.placeholders.noDate');
      }

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) {
        return t('campaigns.common.placeholders.noDate');
      }

      return new Intl.DateTimeFormat(locale.value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    const formatChannel = (value: string | null): string => {
      if (!value) {
        return t('campaigns.common.headers.channelUnknown');
      }
      const key = value.toLowerCase();
      const translationKey = `campaigns.common.channels.${key}`;
      const translated = t(translationKey);
      return translated === translationKey ? key : translated;
    };

    const extractChannelTags = (row: any): string[] => {
      const uniqueChannels = new Set<string>();

      if (Array.isArray(row?.channels)) {
        row.channels.forEach((channel: any) => {
          if (channel?.channel) {
            uniqueChannels.add(channel.channel);
          }
        });
      }

      if (row?.primary_channel) {
        uniqueChannels.add(row.primary_channel);
      }

      return Array.from(uniqueChannels);
    };

    const buildEngagementMetrics = (row: any): CampaignMetricDisplay[] => [
      {
        id: 'open_rate',
        label: t('campaigns.common.metrics.openRate'),
        value: formatPercentage(row.open_rate),
      },
      {
        id: 'click_rate',
        label: t('campaigns.common.metrics.clickRate'),
        value: formatPercentage(row.click_rate),
      },
    ];

    const buildConversionMetrics = (row: any): CampaignMetricDisplay[] => [
      {
        id: 'sales_amount',
        label: t('campaigns.common.metrics.salesAmount'),
        value: formatAmount(row.sales_amount),
      },
      {
        id: 'conversion_rate',
        label: t('campaigns.common.metrics.conversionRate'),
        value: formatPercentage(row.conversion_rate),
      },
    ];

    const buildExpandedMetrics = (row: any): CampaignMetricDisplay[] => [
      {
        id: 'deliveries',
        label: t('campaigns.common.metrics.deliveries'),
        value: formatInteger(row.deliveries),
      },
      {
        id: 'opens',
        label: t('campaigns.common.metrics.opensWithUnique'),
        value: formatCombinedMetric(row.opens, row.open_unique),
      },
      {
        id: 'clicks',
        label: t('campaigns.common.metrics.clicksWithUnique'),
        value: formatCombinedMetric(row.clicks, row.click_unique),
      },
      {
        id: 'sales',
        label: t('campaigns.common.metrics.sales'),
        value: formatInteger(row.sales),
      },
      {
        id: 'bounces',
        label: t('campaigns.common.metrics.bounces'),
        value: formatInteger(row.bounces),
      },
      {
        id: 'spam',
        label: t('campaigns.common.metrics.spam'),
        value: formatInteger(row.spam),
      },
      {
        id: 'unsubscribes',
        label: t('campaigns.common.metrics.unsubscribes'),
        value: formatInteger(row.unsubscribes),
      },
    ];

    const buildChannelMetrics = (channel: any): CampaignMetricDisplay[] => [
      {
        id: 'deliveries',
        label: t('campaigns.common.metrics.deliveries'),
        value: formatInteger(channel.deliveries),
      },
      {
        id: 'open_rate',
        label: t('campaigns.common.metrics.openRate'),
        value: formatPercentage(channel.open_rate),
      },
      {
        id: 'click_rate',
        label: t('campaigns.common.metrics.clickRate'),
        value: formatPercentage(channel.click_rate),
      },
      {
        id: 'sales_amount',
        label: t('campaigns.common.metrics.salesAmount'),
        value: formatAmount(channel.sales_amount),
      },
      {
        id: 'revenue_per_send',
        label: t('campaigns.common.metrics.revenuePerSend'),
        value: formatAmount(channel.revenue_per_send),
      },
    ];

    const statusStyle = (status: string | null | undefined) => getStatusStyleVars(status);
    const statusIcon = (status: string | null | undefined) => getStatusIcon(status);
    const channelTagStyle = (channel: string | null | undefined) => getChannelTagStyleVars(channel);
    const channelIcon = (channel: string | null | undefined) => getChannelIcon(channel);
    const channelCardStyle = (channel: string | null | undefined) => getChannelCardStyleVars(channel);

    return {
      t,
      locale,
      idShop,
      tableRef,
      columns,
      initialSorting,
      persistentFilters,
      openWorkflowSelector,
      showImportModal,
      hideImportModal,
      isImportModalVisible,
      showNewImportModal,
      hideNewImportModal,
      isNewImportModalVisible,
      handleCreateScenario,
      formatPercentage,
      formatInteger,
      formatDateTime,
      formatChannel,
      extractChannelTags,
      buildEngagementMetrics,
      buildConversionMetrics,
      buildExpandedMetrics,
      buildChannelMetrics,
      statusStyle,
      statusIcon,
      channelTagStyle,
      channelIcon,
      channelCardStyle,
      isProcessingAction,
      findPermission,
      formatAmount,
      groupActions,
    };
  },
});
